/**
 * A5 label toolbar: Full label / Matches only toggle, Find, legend, zero note
 * (R7.1, R7.3, R7.4, R7.5). Presentational; Enter/debounce logic lives in the page.
 */
import React, { useId } from 'react';

interface LabelToolbarProps {
  showToggle: boolean;
  viewMode: 'full' | 'matches';
  total: number;
  onViewMode: (m: 'full' | 'matches') => void;
  findText: string;
  onFindTextChange: (t: string) => void;
  onEnter: (shift: boolean) => void;
  onPrev: () => void;
  onNext: () => void;
  termsActive: boolean;
  matchCount: number;
  /** 1-based. */
  activeIndex: number | null;
  liveText: string;
  showMatchLegend: boolean;
  showCepiLegend: boolean;
  zeroNoteKeyword: string | null;
  /** Null hides the button (not the sections path). */
  collapseLabel: 'Collapse all' | 'Expand all' | null;
  onToggleCollapseAll: () => void;
}

const Chevron: React.FC<{ up?: boolean }> = ({ up }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d={up ? 'M3 9l4-4 4 4' : 'M3 5l4 4 4-4'} />
  </svg>
);

const LabelToolbar: React.FC<LabelToolbarProps> = ({
  showToggle, viewMode, total, onViewMode, findText, onFindTextChange, onEnter, onPrev, onNext,
  termsActive, matchCount, activeIndex, liveText, showMatchLegend, showCepiLegend, zeroNoteKeyword,
  collapseLabel, onToggleCollapseAll,
}) => {
  const id = useId();
  const seg = (on: boolean) =>
    `px-3 py-1 text-sm ${on ? 'bg-accent-tint text-accent-deep font-medium' : 'text-ink-2'} disabled:opacity-50`;
  let counter: string | null = null;
  if (termsActive) {
    if (matchCount === 0) counter = 'No matches';
    else if (activeIndex) counter = `${activeIndex}/${matchCount}`;
    else counter = matchCount === 1 ? '1 match' : `${matchCount} matches`;
  }
  const navBtn = 'p-1 rounded text-ink-2 hover:bg-accent-tint disabled:opacity-40';

  return (
    <div className="mb-3 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        {showToggle && (
          <div role="group" aria-label="Label view" className="inline-flex overflow-hidden rounded border border-line-input">
            <button type="button" aria-pressed={viewMode === 'full'} className={seg(viewMode === 'full')} onClick={() => onViewMode('full')}>
              Full label
            </button>
            <button
              type="button"
              aria-pressed={viewMode === 'matches'}
              className={seg(viewMode === 'matches')}
              disabled={total === 0}
              onClick={() => onViewMode('matches')}
            >
              {`Matches only · ${total}`}
            </button>
          </div>
        )}
        <div className="flex items-center gap-1">
          <label className="sr-only" htmlFor={id}>Find in label</label>
          <input
            id={id}
            type="text"
            value={findText}
            placeholder="Find in label"
            className="w-56 rounded border border-line-input px-2 py-1 text-sm"
            onChange={(e) => onFindTextChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onEnter(e.shiftKey);
              }
            }}
          />
          <button type="button" aria-label="Previous match" className={navBtn} disabled={matchCount === 0}
            onMouseDown={(e) => e.preventDefault()} onClick={onPrev}><Chevron up /></button>
          <button type="button" aria-label="Next match" className={navBtn} disabled={matchCount === 0}
            onMouseDown={(e) => e.preventDefault()} onClick={onNext}><Chevron /></button>
          {counter && <span className="font-mono text-xs text-ink-2">{counter}</span>}
          <span className="sr-only" aria-live="polite">{liveText}</span>
        </div>
        {collapseLabel && (
          <button type="button" className="rounded border border-line-input px-3 py-1 text-sm text-ink-2" onClick={onToggleCollapseAll}>{collapseLabel}</button>
        )}
        <div className="ml-auto flex items-center gap-4 text-xs text-ink-2">
          {showMatchLegend && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-3 w-4 rounded-sm bg-match" aria-hidden="true" />Keyword match
            </span>
          )}
          {showCepiLegend && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-3 w-4 bg-accent-ring border-b-2 border-accent" aria-hidden="true" />Cited by CEPI
            </span>
          )}
        </div>
      </div>
      {zeroNoteKeyword && (
        <p className="text-sm text-ink-2">
          {`No exact matches for “${zeroNoteKeyword}” in this label. Keyword search also matches other word forms and synonyms.`}
        </p>
      )}
    </div>
  );
};

export default LabelToolbar;
